import { FunctionComponent, useCallback, useMemo } from 'react';
import { useIam } from '../../../services/IAm/hooks';
import { BookerForm } from '../ContactForms/BookerForm';
import { StepContainer } from '../StepContainer';
import { GuestForm } from '../ContactForms/GuestForm';
import styles from './styles.module.scss';
import { Box } from '../../Box';
import { ValidationError } from '@shared/domain-shared';
import { useCheckoutContext } from '../CheckoutContext';
import React from 'react';
import { FormContext, ArrayFieldset, Fieldset, useForm } from '@cademy-ui/form';
import { TermsAndConditionsField } from './TermsAndConditionsField';
import { BookerIsAttendingField } from './BookerIsAttendingField';
import { MarketingConsentField } from './MarketingConsentField';
import { useInitialFormValues } from '../../CustomForm/useInitialFormValues';
import Card from '../../Card';
import Spinner from '../../Spinner';

type FormShape = any;

export type ContactDetailsProps = {
    actionType?: 'contact details filled' | 'order contact details filled';
};

export const ContactDetailsStep: FunctionComponent<ContactDetailsProps> = ({
    actionType = 'contact details filled',
}) => {
    const { course, educator, checkoutState, submitResponse, error } = useCheckoutContext();
    const { iam, iamReady } = useIam();

    const { data: initialBookerFormValues, isLoading: isLoadingInitialBookerFormValues } =
        useInitialFormValues(course.form_ref || 'system_default');

    const requestContactDetails = useMemo(() => {
        return course.capture_guests === true;
    }, [course]);

    const userId = useMemo(() => {
        if (iam.authenticated) {
            return iam.id;
        }
        return null;
    }, [iam]);

    const onSubmit = useCallback(
        async (formResponse: FormShape) => {
            if (!userId) {
                throw new Error('Unknown user');
            }
            await submitResponse({
                actionType: actionType,
                payload: {
                    bookerId: userId,
                    attendeeFormId: course.guest_form_ref || null,
                    bookerFormId: course.form_ref || null,
                    termsAccepted: false,
                    ...formResponse,
                },
            });
        },
        [actionType, userId, submitResponse, course.form_ref, course.guest_form_ref]
    );

    const initialValues = useMemo(() => {
        if (checkoutState.stepState.contactDetails.status !== 'complete') {
            if (!initialBookerFormValues) {
                return {};
            }
            return { bookerFormResponse: initialBookerFormValues };
        }
        const state = checkoutState.stepState.contactDetails.state;
        const bookerFormResponse = state.booker.formResponse;
        const bookerIsAttending =
            'isAttending' in state.booker ? state.booker.isAttending : undefined;
        const attendeeFormResponses =
            'attendees' in state
                ? state.attendees.map((attendee) => attendee.formResponse)
                : undefined;
        const marketingConsent = state.marketingConsent;
        const termsAccepted = state.termsAccepted;
        return {
            bookerFormResponse,
            bookerIsAttending,
            attendeeFormResponses,
            marketingConsent,
            termsAccepted,
        };
    }, [checkoutState.stepState.contactDetails, initialBookerFormValues]);

    const formContext = useForm<FormShape>({
        onSubmit: onSubmit,
        submitWhenInvalid: true,
        initialValues: initialValues,
    });
    const { values, submit } = formContext;

    const onBackClick = useCallback(() => {
        return submitResponse({ actionType: 'go back', payload: {} });
    }, [submitResponse]);

    const requestedSpaces = useMemo((): number => {
        switch (checkoutState.flow) {
            case 'booking':
            case 'order':
            case 'bookingApplication': {
                if (!checkoutState.stepState.price.state?.spacesRequested) return 1;
                return checkoutState.stepState.price.state.spacesRequested;
            }
            case 'dateBookingRequest':
            case 'groupBookingRequest': {
                if (!checkoutState.stepState.dates.state?.spacesRequested) return 1;
                return checkoutState.stepState.dates.state?.spacesRequested;
            }
            case 'request': {
                if (!checkoutState.stepState.delivery.state?.requestedSpaces) return 1;
                return checkoutState.stepState.delivery.state?.requestedSpaces;
            }
        }
    }, [checkoutState]);

    const numberOfGuests = useMemo(() => {
        if (values.bookerIsAttending === true) {
            return requestedSpaces - 1;
        }
        return requestedSpaces;
    }, [requestedSpaces, values]);

    const bookerFormError = useMemo((): ValidationError | null => {
        if (!error) {
            return null;
        }
        const validationError = ValidationError.createCademyError(error);
        if (validationError) {
            const subError = ValidationError.getNested(
                validationError,
                'payload.bookerFormResponse'
            );
            if (subError.properties.length === 0) {
                return null;
            }
            return subError;
        }
        return null;
    }, [error]);

    const attendeeFormErrors = useMemo((): Array<ValidationError | null> => {
        const emptyErrors = new Array(numberOfGuests).fill(null);
        if (!error) {
            return emptyErrors;
        }
        const validationError = ValidationError.createCademyError(error);
        if (validationError) {
            return emptyErrors.map((_, index) => {
                const subError = ValidationError.getNested(
                    validationError,
                    `payload.attendeeFormResponses.${index}`
                );
                if (subError.properties.length === 0) {
                    return null;
                }
                return subError;
            });
        }
        return emptyErrors;
    }, [error, numberOfGuests]);

    const continueDisabled = useMemo(() => {
        // TODO: Implement when we have the new form logic so we can react to dirty/touched properties
        return false;
    }, []);

    if (!iamReady || isLoadingInitialBookerFormValues) {
        return <Spinner innerClassName={styles.spinnerIn} outerClassName={styles.spinnerOut} />;
    }

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
            }}
            aria-label="Contact information"
        >
            <FormContext.Provider value={formContext}>
                <StepContainer
                    course={course}
                    optimisticCheckoutState={checkoutState}
                    continueDisabled={continueDisabled}
                    onContinueClick={submit}
                    onBackClick={onBackClick}
                >
                    <div className={styles.container}>
                        <div>
                            <fieldset
                                aria-label="My contact details"
                                className={styles.bookerFormContainer}
                            >
                                <h1>Contact Info</h1>
                                <Card className={styles.loggedInAsCard}>
                                    You are logged in as {iam.authenticated && iam.email}
                                    <a
                                        href={`/logout?redirect=${encodeURIComponent(window.location.href)}`}
                                    >
                                        Log out
                                    </a>
                                </Card>
                                <Fieldset name="bookerFormResponse">
                                    <BookerForm
                                        error={bookerFormError}
                                        formId={course.form_ref || null}
                                    />
                                </Fieldset>
                            </fieldset>
                        </div>
                        {requestContactDetails === true ? (
                            <BookerIsAttendingField
                                name="bookerIsAttending"
                                courseKeyword={course.keyword}
                                courseType={course.type}
                            />
                        ) : null}
                        {requestContactDetails === true && numberOfGuests >= 1 ? (
                            <div className={styles.attendeesContainer}>
                                <ArrayFieldset name="attendeeFormResponses">
                                    {new Array(numberOfGuests).fill(null).map((_, index) => {
                                        const error = attendeeFormErrors[index];
                                        return (
                                            <Box
                                                key={index}
                                                className={styles.attendeeForm}
                                                variant="tinted"
                                            >
                                                <h3 className={styles.requiredHeading}>
                                                    {course.type === 'on-demand'
                                                        ? 'Participant'
                                                        : 'Attendee'}{' '}
                                                    {index + 1} Details
                                                </h3>
                                                <GuestForm
                                                    formId={course.guest_form_ref || null}
                                                    error={error}
                                                />
                                            </Box>
                                        );
                                    })}
                                </ArrayFieldset>
                            </div>
                        ) : null}
                        <MarketingConsentField
                            name="marketingConsent"
                            educator={educator}
                            origin={checkoutState.origin}
                        />
                        <TermsAndConditionsField
                            name="termsAccepted"
                            educator={educator}
                            origin={checkoutState.origin}
                        />
                    </div>
                </StepContainer>
            </FormContext.Provider>
        </form>
    );
};
